import moment from 'moment-timezone';
import { SingleProfitCalculationApiResponse } from '../../../ProfitCalculations/types';
import { DeepTx, MrcPhase, TxItem } from './txHelpers';
import {
  fmtBalance,
  fmtCompact,
  fmtDate,
  fmtDateShort,
  fmtMultiplier,
  fmtPct,
  fmtPrice,
  fmtQty,
  fmtTimespan,
  fmtTimespanShort,
  isCore,
  txStableFraction,
  vsBuyAndHold,
} from './derived/format';
import {
  HeatmapCell,
  HistogramBin,
  TimePoint,
  allProfits,
  computeHeatmap,
  computeStreaks,
  equityCurve,
  histogram,
  median,
  monthlyAverageProfit,
  streakSeries,
} from './derived/aggregations';
import {
  avgProfit as computeAvgProfit,
  avgTradeDurationMs,
  buildTransactionList,
} from './derived/transactions';
import {
  buildLiveStateFromCurrent,
  buildMrcByTxId,
} from './derived/mrcEpisodes';

export type { TxItem, DeepTx, MrcPhase };

export interface LiveStateDisplay {
  currentPrice: string;
  currentProfit: string;
  currentPriceChange: string;
  stableBalance: string;
  assetBalance: string;
  /** Fraction (0..1) of the live balance value held in stable. */
  balanceStableFraction: number;
  openedAt: string;
  openDurationDays: number;
  latestOrderType: 'BUY' | 'SELL';
}

export interface DisplayData {
  // Header
  headerDate: string;
  assetPair: string;
  dateRange: string;
  duration: string;
  durationShort: string;

  // Hero
  startBalance: string;
  endBalance: string;
  startBalanceAsset: string;
  endBalanceAsset: string;
  balanceArrow: string;
  vsBuyAndHold: string;
  stableProfit: string;
  assetProfit: string;
  score: string | null;
  priceChange: string;
  totalFees: string;
  txCount: number;
  coreCount: number;
  winRate: string;
  wins: number;
  losses: number;
  takeProfits: number;
  stopLosses: number;

  // LiveState
  currentProfit: string;
  currentPriceChange: string;
  liveState: LiveStateDisplay;

  // Statistics
  medianProfit: string;
  bestTrade: string;
  worstTrade: string;
  avgDuration: string;
  avgProfit: string;
  longestWinStreak: number;
  longestLossStreak: number;
  avgStreak: string;
  monthlyAvgStable: string;
  totalMonths: number;
  profitableMonths: number;

  // Config
  timeframes: Record<string, string>;
  complexParams: Record<string, string>;
  indicatorConfig: {
    opposite: Record<string, string>;
    same: Record<string, string>;
  };
  mrcStateConfig: {
    period: Record<string, string>;
    threshold: Record<string, string>;
  };

  profitPeriods: {
    stable: Record<string, string>;
    asset: Record<string, string>;
    avgStable: Record<string, string>;
    avgAsset: Record<string, string>;
  };

  // Transactions
  transactionList: TxItem[];
  deepByTxId: Record<number, DeepTx>;
  mrcByTxId: Record<number, MrcPhase[] | null>;
  orderedTxIds: number[];

  // Chart data
  equityCurve: { stable: TimePoint[]; asset: TimePoint[]; price: TimePoint[] };
  heatmap: Record<number, Record<number, HeatmapCell>>;
  histogram: HistogramBin[];
  streakSeries: Array<{ win: boolean; profit: number }>;

  // Raw passthrough for chart components that need full SPC access
  raw: SingleProfitCalculationApiResponse;

  // ── Optional, subscription-only addition ───────────────────────────────
  // Undefined on the result page, so its rendering/behaviour is unchanged.
  // Set by the subscription redesign adapter to subdue accent greens and colour
  // profit numbers by sign.
  subscriptionView?: boolean;
}

const mapTimeframes = (
  spc: SingleProfitCalculationApiResponse,
): Record<string, string> => ({
  higherT: spc.higherTimeFrame,
  shorterT: spc.shorterTimeFrame,
  multiT: spc.multiOrderTimeFrame,
});

const mapComplexParams = (
  spc: SingleProfitCalculationApiResponse,
): Record<string, string> => ({
  cbrt: String(spc.cbrt),
  shorterTInterval: String(spc.shorterTimeFrameInterval),
});

const mapIndicatorSide = (
  spc: SingleProfitCalculationApiResponse,
  side: 'oppositeOrder' | 'sameOrder',
): Record<string, string> => {
  const config = spc.indicatorConfig;
  if (!config) {
    return {
      parentTimeframe: '—',
      parentChildTimeframe: '—',
      parentPeriod: '—',
      parentThreshold: '—',
      parentCandleType: '—',
      takeProfitSize: '—',
      stopLossThreshold: '—',
    };
  }
  return {
    parentTimeframe: config[`${side}TimeFrame`],
    parentChildTimeframe: config[`${side}ChildTimeFrame`],
    parentPeriod: String(config[`${side}Period`]),
    parentThreshold: String(config[`${side}Threshold`]),
    parentCandleType: config[`${side}CandleType`],
    takeProfitSize: String(config[`${side}TakeProfitSize`]),
    stopLossThreshold: String(config[`${side}StoplossThreshold`]),
  };
};

type MrcRsiConfig = {
  higherTRsiPeriod?: number;
  shorterTRsiPeriod?: number;
  multiTRsiPeriod?: number;
  higherTRsiThreshold?: number;
  shorterTRsiThreshold?: number;
  multiTRsiThreshold?: number;
};

const mapMrcStateConfig = (
  spc: SingleProfitCalculationApiResponse,
): { period: Record<string, string>; threshold: Record<string, string> } => {
  const config = spc.mrcRsiConfig as MrcRsiConfig | null | undefined;
  const fmt = (value: number | undefined): string =>
    value === undefined ? '—' : String(value);
  return {
    period: {
      higherT: fmt(config?.higherTRsiPeriod),
      shorterT: fmt(config?.shorterTRsiPeriod),
      multiT: fmt(config?.multiTRsiPeriod),
    },
    threshold: {
      higherT: fmt(config?.higherTRsiThreshold),
      shorterT: fmt(config?.shorterTRsiThreshold),
      multiT: fmt(config?.multiTRsiThreshold),
    },
  };
};

const buildProfitPeriods = (spc: SingleProfitCalculationApiResponse) => {
  const since = spc.stableProfitSince;
  const stable: Record<string, string> = {
    '1d': fmtPct(since.day),
    '1w': fmtPct(since.week),
    '1M': fmtPct(since.month),
    '1Y': fmtPct(since.year),
    '2Y': fmtPct(since.twoYear),
    '5Y': fmtPct(since.FiveYear),
  };
  const asset: Record<string, string> = {
    '1d': fmtPct(spc.assetProfitSince.day),
    '1w': fmtPct(spc.assetProfitSince.week),
    '1M': fmtPct(spc.assetProfitSince.month),
    '1Y': fmtPct(spc.assetProfitSince.year),
    '2Y': fmtPct(spc.assetProfitSince.twoYear),
    '5Y': fmtPct(spc.assetProfitSince.FiveYear),
  };
  const avgStable: Record<string, string> = {
    '1d': fmtPct(spc.averageStableProfit.daily),
    '1w': fmtPct(spc.averageStableProfit.weekly),
    '1M': fmtPct(spc.averageStableProfit.monthly),
    '1Y': fmtPct(spc.averageStableProfit.yearly),
    '2Y': fmtPct(spc.stableProfitSince.twoYear / 2),
    '5Y': fmtPct(spc.stableProfitSince.FiveYear / 5),
  };
  const avgAsset: Record<string, string> = {
    '1d': fmtPct(spc.averageAssetProfit.daily),
    '1w': fmtPct(spc.averageAssetProfit.weekly),
    '1M': fmtPct(spc.averageAssetProfit.monthly),
    '1Y': fmtPct(spc.averageAssetProfit.yearly),
    '2Y': fmtPct(spc.assetProfitSince.twoYear / 2),
    '5Y': fmtPct(spc.assetProfitSince.FiveYear / 5),
  };
  return { stable, asset, avgStable, avgAsset };
};

const buildLiveStateDisplay = (
  spc: SingleProfitCalculationApiResponse,
): LiveStateDisplay => {
  const prices = Object.values(spc.priceHistory);
  const currentPriceNumber = prices[prices.length - 1] ?? 0;
  const lastCore = buildLiveStateFromCurrent(spc);
  const lastCoreTimestamp =
    lastCore?.transactionTimestamp ?? lastCore?.timestamp ?? 0;
  const openDurationMs = lastCoreTimestamp ? Date.now() - lastCoreTimestamp : 0;
  // Balances + split mirror the most recent transaction exactly — nothing has
  // happened since, so the live state must show that transaction's values.
  const latestTx = [...spc.transactions].sort(
    (a, b) =>
      (b.transactionTimestamp ?? b.timestamp) -
      (a.transactionTimestamp ?? a.timestamp),
  )[0];
  return {
    currentPrice: fmtPrice(currentPriceNumber),
    currentProfit: fmtPct(spc.currentProfit, true),
    currentPriceChange: fmtPct(spc.currentPriceChange, true),
    stableBalance: fmtBalance(
      latestTx ? latestTx.balanceInUsdt : spc.endBalanceInUsdt,
    ),
    assetBalance: fmtQty(
      latestTx ? latestTx.balanceInAsset : spc.endBalanceInAsset,
    ),
    balanceStableFraction: latestTx ? txStableFraction(latestTx) : 0,
    openedAt: lastCoreTimestamp ? `since ${fmtDate(lastCoreTimestamp)}` : '—',
    openDurationDays: Math.floor(openDurationMs / 86_400_000),
    latestOrderType: lastCore?.orderType ?? 'BUY',
  };
};

const buildHero = (spc: SingleProfitCalculationApiResponse) => {
  const startTimestamp = new Date(spc.startTimestamp).valueOf();
  const endTimestamp = new Date(spc.endTimestamp).valueOf();
  const successful = spc.amountOfSuccessfulOrders;
  const failed = spc.amountOfFailedOrders;
  const winRateValue =
    spc.amountOfOrders > 1 ? successful / (spc.amountOfOrders - 1) : 0;
  const prices = Object.values(spc.priceHistory);
  const firstPrice = prices[0] ?? 1;
  const lastPrice = prices[prices.length - 1] ?? firstPrice;
  const scoreRaw = (spc as { score?: number }).score;
  const score =
    typeof scoreRaw === 'number' && Number.isFinite(scoreRaw)
      ? scoreRaw.toFixed(2)
      : null;
  return {
    headerDate: moment().utc().format('DD MMM YYYY').toUpperCase(),
    assetPair: `${spc.baseAsset}/${spc.quoteAsset}`,
    dateRange: `${fmtDateShort(startTimestamp)} – ${fmtDateShort(
      endTimestamp,
    )}`,
    duration: fmtTimespan(endTimestamp - startTimestamp),
    durationShort: fmtTimespanShort(endTimestamp - startTimestamp),
    startBalance: fmtBalance(spc.startBalanceInUsdt),
    endBalance: fmtBalance(spc.endBalanceInUsdt),
    startBalanceAsset: fmtQty(
      Object.values(spc.endBalanceInAssetHistory)[0] ?? 0,
    ),
    endBalanceAsset: fmtQty(spc.endBalanceInAsset),
    balanceArrow: `${fmtCompact(spc.startBalanceInUsdt)}→${fmtCompact(
      spc.endBalanceInUsdt,
    )}`,
    vsBuyAndHold: fmtMultiplier(
      vsBuyAndHold(
        spc.startBalanceInUsdt,
        spc.endBalanceInUsdt,
        firstPrice,
        lastPrice,
      ),
    ),
    stableProfit: fmtPct(spc.totalTradingStableProfit),
    assetProfit: fmtPct(spc.totalTradingAssetProfit),
    score,
    priceChange: fmtPct(spc.currentPriceChange, true),
    totalFees: fmtBalance(spc.totalAmountOfFeesPaid),
    txCount: spc.amountOfOrders,
    winRate: fmtPct(winRateValue),
    wins: successful,
    losses: failed,
    takeProfits: spc.amountOfTakeProfitOrders,
    stopLosses: spc.amountOfStopLossOrders,
  };
};

export const toDisplayData = (
  spc: SingleProfitCalculationApiResponse,
): DisplayData => {
  const profits = allProfits(spc);
  const streaks = computeStreaks(profits);
  const monthlyAvg = monthlyAverageProfit(spc);
  const txData = buildTransactionList(spc);
  const hero = buildHero(spc);

  // Win/loss + per-trade stats span every transaction (incl. TP/SL).
  const coreCount = spc.transactions.filter((tx) =>
    isCore(tx.transactionType),
  ).length;
  const wins = profits.filter((profit) => profit > 0).length;
  const losses = profits.length - wins;
  const winRate = profits.length ? wins / profits.length : 0;
  const bestTradeValue = profits.length ? Math.max(...profits) : 0;
  const worstTradeValue = profits.length ? Math.min(...profits) : 0;
  const avgDurationDays = Math.round(avgTradeDurationMs(spc) / 86_400_000);

  return {
    ...hero,
    wins,
    losses,
    winRate: fmtPct(winRate),
    coreCount,

    currentProfit: fmtPct(spc.currentProfit, true),
    currentPriceChange: fmtPct(spc.currentPriceChange, true),
    liveState: buildLiveStateDisplay(spc),

    medianProfit: fmtPct(median(profits), true),
    bestTrade: fmtPct(bestTradeValue, true),
    worstTrade: fmtPct(worstTradeValue, true),
    avgDuration: `${avgDurationDays} ${avgDurationDays === 1 ? 'day' : 'days'}`,
    avgProfit: fmtPct(computeAvgProfit(spc), true),
    longestWinStreak: streaks.longestWin,
    longestLossStreak: streaks.longestLoss,
    avgStreak: streaks.avgStreak.toFixed(1).replace('.', ','),
    monthlyAvgStable: fmtPct(monthlyAvg.avg),
    totalMonths: monthlyAvg.totalMonths,
    profitableMonths: monthlyAvg.profitableMonths,

    timeframes: mapTimeframes(spc),
    complexParams: mapComplexParams(spc),
    indicatorConfig: {
      opposite: mapIndicatorSide(spc, 'oppositeOrder'),
      same: mapIndicatorSide(spc, 'sameOrder'),
    },
    mrcStateConfig: mapMrcStateConfig(spc),

    profitPeriods: buildProfitPeriods(spc),

    transactionList: txData.list,
    deepByTxId: txData.deepByTxId,
    orderedTxIds: txData.orderedIds,
    mrcByTxId: buildMrcByTxId(spc),

    equityCurve: equityCurve(spc),
    heatmap: computeHeatmap(spc),
    histogram: histogram(profits),
    streakSeries: streakSeries(profits),

    raw: spc,
  };
};
